import { useCallback, useEffect, useRef, useState } from 'react';
import { DOCUMENT_EXTENSIONS, MAX_DOCUMENTS_PER_MESSAGE, MAX_DOCUMENT_BYTES, type DisplayDocument, type DocumentSummary } from '../types/agent';

/** One file picked in the composer (2026-09-10): uploaded straight away, sent by id with the next message. */
export interface Attachment {
  localId: string;
  name: string;
  size: number;
  /** `processing`: uploaded, the server is transcribing scanned pages (polled until ready). */
  status: 'uploading' | 'processing' | 'ready' | 'error';
  summary?: DocumentSummary;
  /** i18n key (`attachments.errors.*`) or the server's `detail`. */
  error?: string;
}

export interface AttachmentsApi {
  uploadDocument(file: File, threadId: string | null, signal?: AbortSignal): Promise<DocumentSummary>;
  deleteDocument(documentId: string): Promise<void>;
  getDocument?(documentId: string): Promise<DocumentSummary>;
}

/** Poll interval while a document is `processing` (OCR in the background), and the give-up horizon. */
export const OCR_POLL_MS = 2500;
export const OCR_POLL_MAX_MS = 15 * 60 * 1000;
export const ERR_OCR = 'attachments.errors.ocr';

let seq = 0;
const nextId = () => `att-${++seq}`;

export const ERR_TYPE = 'attachments.errors.type';
export const ERR_SIZE = 'attachments.errors.size';
export const ERR_COUNT = 'attachments.errors.count';
export const ERR_UPLOAD = 'attachments.errors.upload';

export function isSupportedFile(name: string): boolean {
  const ext = name.includes('.') ? name.split('.').pop()!.toLowerCase() : '';
  return (DOCUMENT_EXTENSIONS as readonly string[]).includes(ext);
}

export const ACCEPT_ATTRIBUTE = DOCUMENT_EXTENSIONS.map((e) => `.${e}`).join(',');

/** Attachment state of the composer: pick → upload (concurrently) → ready; remove deletes an unsent
 *  upload; `take()` hands the ready documents to the message and clears the list. */
export function useAttachments(api: AttachmentsApi, getThreadId: () => string | null) {
  const [items, setItems] = useState<Attachment[]>([]);
  const aborts = useRef(new Map<string, AbortController>());

  const patch = useCallback((localId: string, fn: (a: Attachment) => Attachment) => {
    setItems((list) => list.map((a) => (a.localId === localId ? fn(a) : a)));
  }, []);

  const add = useCallback(
    (files: File[]) => {
      if (!files.length) return;
      setItems((list) => {
        const next = [...list];
        for (const file of files) {
          const att: Attachment = { localId: nextId(), name: file.name, size: file.size, status: 'uploading' };
          if (next.filter((a) => a.status !== 'error').length >= MAX_DOCUMENTS_PER_MESSAGE) {
            next.push({ ...att, status: 'error', error: ERR_COUNT });
            continue;
          }
          if (!isSupportedFile(file.name)) {
            next.push({ ...att, status: 'error', error: ERR_TYPE });
            continue;
          }
          if (file.size > MAX_DOCUMENT_BYTES) {
            next.push({ ...att, status: 'error', error: ERR_SIZE });
            continue;
          }
          next.push(att);
          const abort = new AbortController();
          aborts.current.set(att.localId, abort);
          void api
            .uploadDocument(file, getThreadId(), abort.signal)
            .then((summary) => {
              if (summary.status === 'processing') {
                patch(att.localId, (a) => ({ ...a, status: 'processing', summary }));
                return pollUntilReady(api, summary.document_id, abort.signal).then((done) => patch(att.localId, (a) => (a.status === 'processing' ? { ...a, status: 'ready', summary: done } : a)));
              }
              patch(att.localId, (a) => ({ ...a, status: 'ready', summary }));
              return undefined;
            })
            .catch((err: unknown) => {
              if (abort.signal.aborted) return;
              const detail = err instanceof Error && err.message ? err.message : '';
              patch(att.localId, (a) => ({ ...a, status: 'error', error: detail || ERR_UPLOAD }));
            })
            .finally(() => aborts.current.delete(att.localId));
        }
        return next;
      });
    },
    [api, getThreadId, patch],
  );

  const remove = useCallback(
    (localId: string) => {
      setItems((list) => {
        const att = list.find((a) => a.localId === localId);
        if (att?.status === 'uploading' || att?.status === 'processing') aborts.current.get(localId)?.abort();
        if (att?.summary) void api.deleteDocument(att.summary.document_id).catch(() => undefined);
        return list.filter((a) => a.localId !== localId);
      });
    },
    [api],
  );

  /** Ready documents for the message being sent; the list is cleared (errors included). */
  const take = useCallback((): DisplayDocument[] => {
    const ready = items.filter((a): a is Attachment & { summary: DocumentSummary } => a.status === 'ready' && !!a.summary).map((a) => a.summary);
    setItems([]);
    return ready;
  }, [items]);

  const clearErrors = useCallback(() => setItems((list) => list.filter((a) => a.status !== 'error')), []);

  useEffect(() => () => aborts.current.forEach((a) => a.abort()), []);

  const uploading = items.some((a) => a.status === 'uploading' || a.status === 'processing');
  const ready = items.filter((a) => a.status === 'ready').length;
  return { items, add, remove, take, clearErrors, uploading, ready };
}

/** GET the document every OCR_POLL_MS until it leaves `processing`; rejects on abort or past OCR_POLL_MAX_MS. */
export async function pollUntilReady(api: AttachmentsApi, documentId: string, signal: AbortSignal): Promise<DocumentSummary> {
  const started = Date.now();
  for (;;) {
    await new Promise<void>((resolve, reject) => {
      const t = setTimeout(resolve, OCR_POLL_MS);
      signal.addEventListener('abort', () => { clearTimeout(t); reject(new DOMException('aborted', 'AbortError')); }, { once: true });
    });
    if (!api.getDocument) throw new Error(ERR_OCR);
    const doc = await api.getDocument(documentId);
    if (doc.status !== 'processing') return doc;
    if (Date.now() - started > OCR_POLL_MAX_MS) throw new Error(ERR_OCR);
  }
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
